import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Sacco, SaccoStats, User, PaginationMeta, SaccoCreateForm, SaccoUpdateForm } from '@/types'
import { saccosApi } from '@/api/saccos'

export const useSaccosStore = defineStore('saccos', () => {
  // State
  const saccos = ref<Sacco[]>([])
  const currentSacco = ref<Sacco | null>(null)
  const currentSaccoStats = ref<SaccoStats | null>(null)
  const saccoUsers = ref<User[]>([])
  const meta = ref<PaginationMeta | null>(null)
  const usersMeta = ref<PaginationMeta | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Actions
  async function fetchSaccos(params: { page?: number; per_page?: number; search?: string } = {}) {
    loading.value = true
    error.value = null

    try {
      const response = await saccosApi.list(params)
      saccos.value = response.data
      meta.value = response.meta
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch saccos'
    } finally {
      loading.value = false
    }
  }

  async function fetchSacco(id: string) {
    loading.value = true
    error.value = null

    try {
      currentSacco.value = await saccosApi.get(id)
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch sacco'
    } finally {
      loading.value = false
    }
  }

  async function fetchSaccoStats(id: string) {
    try {
      currentSaccoStats.value = await saccosApi.getStats(id)
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch stats'
    }
  }

  async function createSacco(data: SaccoCreateForm) {
    loading.value = true
    error.value = null

    try {
      const sacco = await saccosApi.create(data)
      saccos.value.unshift(sacco)
      return sacco
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to create sacco'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateSacco(id: string, data: SaccoUpdateForm) {
    loading.value = true
    error.value = null

    try {
      const sacco = await saccosApi.update(id, data)
      currentSacco.value = sacco
      const index = saccos.value.findIndex(s => s.id === id)
      if (index !== -1) {
        saccos.value[index] = sacco
      }
      return sacco
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update sacco'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function deleteSacco(id: string) {
    loading.value = true
    error.value = null

    try {
      await saccosApi.delete(id)
      saccos.value = saccos.value.filter(s => s.id !== id)
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete sacco'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchSaccoUsers(id: string, params: { page?: number; per_page?: number; search?: string; role?: string; status?: string } = {}) {
    loading.value = true
    error.value = null

    try {
      const response = await saccosApi.listUsers(id, params)
      saccoUsers.value = response.data
      usersMeta.value = response.meta
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch users'
    } finally {
      loading.value = false
    }
  }

  async function addHostname(saccoId: string, fqdn: string) {
    try {
      const hostname = await saccosApi.addHostname(saccoId, fqdn)
      if (currentSacco.value && currentSacco.value.id === saccoId) {
        currentSacco.value.hostnames = [...(currentSacco.value.hostnames || []), hostname]
      }
      return hostname
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to add hostname'
      throw err
    }
  }

  async function removeHostname(saccoId: string, hostnameId: string) {
    try {
      await saccosApi.removeHostname(saccoId, hostnameId)
      if (currentSacco.value && currentSacco.value.id === saccoId) {
        currentSacco.value.hostnames = currentSacco.value.hostnames?.filter(h => h.id !== hostnameId)
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to remove hostname'
      throw err
    }
  }

  function clearCurrent() {
    currentSacco.value = null
    currentSaccoStats.value = null
    saccoUsers.value = []
  }

  return {
    saccos,
    currentSacco,
    currentSaccoStats,
    saccoUsers,
    meta,
    usersMeta,
    loading,
    error,
    fetchSaccos,
    fetchSacco,
    fetchSaccoStats,
    createSacco,
    updateSacco,
    deleteSacco,
    fetchSaccoUsers,
    addHostname,
    removeHostname,
    clearCurrent,
  }
})
